import { Dialog, DialogDescription, DialogTitle, DialogContent } from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { useEffect, useRef, type PropsWithChildren, type JSX } from 'react'

export const ModalDialog = ({ children, isVisible, onDismiss, className }: {
  isVisible: boolean
  onDismiss?: () => void
  className?: string
} & PropsWithChildren): JSX.Element => {
  const popupRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isVisible) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      const popup = popupRef.current
      const active = document.activeElement

      if (!popup || !(active instanceof HTMLElement)) {
        return
      }

      if (!popup.contains(active)) {
        return
      }

      // First Escape: blur focus out of the dialog so a second Escape can close.
      active.blur()
      event.stopImmediatePropagation()
      event.preventDefault()
    }

    document.addEventListener('keydown', handleKeyDown, { capture: true })

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true })
    }
  }, [isVisible])

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss?.()
        }
      }}
    >
      <DialogDescription />
      <DialogTitle />
      {/*
        DialogContent carries max-w-lg of its own, which caps the popup at 512px
        no matter what w-* it is given. Callers that need a wider dialog pass
        their own max-w-*, which tailwind-merge lets win over the default.
      */}
      <DialogContent ref={popupRef} className={cn('p-0 w-[94vw]', className)}>
        {children}
      </DialogContent>
    </Dialog>
  )
}
